import { Box } from "@mui/material";
import { useMemo, useState, type ReactNode } from "react";

import { GeneratedTile } from "./GeneratedTile";
import { GridHeader } from "./GridHeader";
import { LoadingSkeletonGrid } from "./LoadingSkeletonGrid";
import { StateHint } from "./StateHint";
import { gridColumnsSx, type ColumnCount } from "./tileStyles";
import type { GeneratedItem } from "@/features/Generations/types/generatedItem";

export interface GeneratedThumbnailGridProps {
  /** Generated batch-job results. */
  items: GeneratedItem[];
  loading?: boolean;
  error?: string | null;
  defaultColumns?: ColumnCount;
  /** Fired when the user removes or adds back a tile; the caller persists it. */
  onToggleAccept?: (id: string, accepted: boolean) => void;
  onSaveCollection?: (acceptedIds: string[]) => void;
  saveDisabled?: boolean;
  notice?: ReactNode;
}

const isAccepted = (item: GeneratedItem) =>
  item.status === "generated" && item.accepted !== false;

export function GeneratedThumbnailGrid({
  items,
  loading = false,
  error = null,
  defaultColumns = 4,
  onToggleAccept,
  onSaveCollection,
  saveDisabled = false,
  notice,
}: GeneratedThumbnailGridProps) {
  const [columns, setColumns] = useState<ColumnCount>(defaultColumns);
  const failedCount = useMemo(
    () => items.filter((item) => item.status === "failed").length,
    [items],
  );
  const acceptedIds = useMemo(
    () => items.filter(isAccepted).map((i) => i.id),
    [items],
  );

  const toggleAccept = (item: GeneratedItem) => {
    if (item.status === "failed") return;
    onToggleAccept?.(item.id, !isAccepted(item));
  };

  return (
    <Box
      data-testid="generated-thumbnail-grid"
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        gap: 5,
        px: 6,
        py: 5,
      }}
    >
      <GridHeader
        total={items.length}
        acceptedCount={acceptedIds.length}
        failedCount={failedCount}
        columns={columns}
        onColumnsChange={setColumns}
        onSaveCollection={() => onSaveCollection?.(acceptedIds)}
        saveDisabled={saveDisabled}
      />

      {notice}

      {loading ? (
        <LoadingSkeletonGrid columns={columns} />
      ) : error ? (
        <StateHint testId="generated-thumbnail-grid-error" tone="error">
          {error || "Something went wrong loading the collection."}
        </StateHint>
      ) : items.length === 0 ? (
        <StateHint testId="generated-thumbnail-grid-empty" tone="secondary">
          No generated images to review yet.
        </StateHint>
      ) : (
        <Box
          data-testid="generated-thumbnail-grid-items"
          sx={gridColumnsSx(columns)}
        >
          {items.map((item) => (
            <GeneratedTile
              key={item.id}
              item={item}
              accepted={isAccepted(item)}
              onToggle={() => toggleAccept(item)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
